import { Alert } from 'antd';
import { useEffect, useState } from 'react';
import styles from '../styles.module.scss';

/**
 * Kích thước chuẩn của ảnh banner. App vẽ banner Trang chủ theo đúng tỉ lệ này
 * (HP-Media/src/app/features/home/tokens.ts → HOME_LAYOUT.bannerAspect = 3) —
 * đổi một bên thì đổi cả bên kia.
 */
export const BANNER_SIZE = { width: 1200, height: 400 };
const RATIO = BANNER_SIZE.width / BANNER_SIZE.height;
/** Lệch tỉ lệ quá 5% là app cắt mất phần thấy rõ. */
const RATIO_TOLERANCE = 0.05;
/** Màn rộng nhất (~400pt × 3) cần ít nhất ngần này điểm ảnh để không nhoè. */
const MIN_WIDTH = 1080;

const SIZE_TEXT = `${BANNER_SIZE.width} × ${BANNER_SIZE.height} px`;

/** Một dòng nhắc trên trang danh sách banner. */
export const BannerSizeTip = () => (
  <Alert
    type="info"
    showIcon
    className={styles.sizeTip}
    message={
      <>
        Ảnh banner thiết kế theo <b>{SIZE_TEXT}</b> (tỉ lệ 3:1) để hiện đủ trên Trang chủ của app,
        không bị cắt.
      </>
    }
  />
);

/** Ghi chú đầy đủ dưới nhãn "Ảnh" trong form Thêm/Sửa banner. */
export const BannerSizeNote = () => (
  <div className={styles.sizeNote}>
    <div>
      Kích thước chuẩn: <b>{SIZE_TEXT}</b> (tỉ lệ 3:1), JPG hoặc PNG, nên dưới 1 MB.
    </div>
    <ul>
      <li>Trang chủ: ảnh hiện đủ, không bị cắt (thẻ đầu trang và mục Thông báo).</li>
      <li>Góc ảnh được bo tròn trên app: chừa lề khoảng 60 px quanh mép, đừng đặt chữ sát góc.</li>
      <li>
        Nếu chọn thêm Liveshow hoặc PK: dải ở đó thấp hơn, ảnh bị cắt khoảng 60–80 px phía trên và
        phía dưới — đặt chữ quan trọng ở dải giữa.
      </li>
    </ul>
  </div>
);

/** Xem trước ảnh trong khung 3:1 giống app + cảnh báo khi ảnh lệch kích thước chuẩn. */
export const BannerPreview = ({ src }: { src?: string }) => {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    setSize(null);
    if (!src) return;
    const img = new Image();
    img.onload = () => setSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
    return () => {
      img.onload = null;
    };
  }, [src]);

  if (!src) return null;

  const ratio = size ? size.w / size.h : RATIO;
  const off = Math.abs(ratio - RATIO) / RATIO > RATIO_TOLERANCE;
  const small = !!size && size.w < MIN_WIDTH;

  return (
    <div className={styles.preview}>
      <div className={styles.previewLabel}>
        Xem trước trên Trang chủ của app
        {size && (
          <span>
            {' '}
            · ảnh của bạn {size.w} × {size.h} px
          </span>
        )}
      </div>
      <div className={styles.previewFrame}>
        <img src={src} alt="" />
      </div>
      {off && (
        <Alert
          type="warning"
          showIcon
          className={styles.previewAlert}
          message={`Ảnh không đúng tỉ lệ 3:1 nên app sẽ cắt bớt ${
            ratio > RATIO ? 'hai bên trái, phải' : 'phía trên và phía dưới'
          } — khung xem trước ở trên là phần còn lại. Nên làm lại theo ${SIZE_TEXT}.`}
        />
      )}
      {!off && small && (
        <Alert
          type="warning"
          showIcon
          className={styles.previewAlert}
          message={`Ảnh hơi nhỏ, có thể bị nhoè trên điện thoại màn lớn. Nên dùng ${SIZE_TEXT}.`}
        />
      )}
    </div>
  );
};
